import { Alert, Button, Modal } from "antd";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Impedimento } from "../../../utils/puedeInscribirse";
import { ResumenAvisos } from "../../../utils/avisosPostulacion";
import { PasoConEstado } from "./pasosPostulacion";

interface IProps {
  visible: boolean;
  pasos: PasoConEstado[];
  /** Lo que impide inscribir. Mientras haya uno, no se puede confirmar. */
  impedimentos: Impedimento[];
  /** Lo que se podria subsanar o rechazar: advierte, pero no bloquea. */
  resumen: ResumenAvisos;
  inscribiendo: boolean;
  alCerrar: () => void;
  alConfirmar: () => void;
  irAlPaso: (clave: string) => void;
}

/**
 * El recuento de la propuesta, justo antes de inscribirla.
 *
 * Separa dos cosas que no son iguales: lo que impide inscribir, que hay que
 * resolver ahora, y lo que quedaria para subsanar o seria causal de rechazo,
 * que no bloquea porque la propuesta se puede inscribir incompleta y
 * corregirse dentro del plazo. Antes esto se descubria de a un aviso por
 * intento, tocando el boton tantas veces como cosas faltaran.
 */
export const ModalRecuento = ({
  visible,
  pasos,
  impedimentos,
  resumen,
  inscribiendo,
  alCerrar,
  alConfirmar,
  irAlPaso,
}: IProps) => {
  const puedeInscribir = impedimentos.length === 0;

  return (
    <Modal
      visible={visible}
      onCancel={alCerrar}
      width={700}
      footer={null}
      title={
        <span className="font-family-Montserrat-SemiBold font-size-18px">
          Revisa tu propuesta antes de inscribirla
        </span>
      }
    >
      <div className="font-family-Montserrat-Regular font-size-14px">
        <div className="row mb-3">
          {pasos.map((paso) => (
            <div key={paso.clave} className="col-12 d-flex align-items-center mb-2">
              {paso.completo ? (
                <CheckCircleOutlined className="font-color-00904C font-size-16px me-2" />
              ) : (
                <InfoCircleOutlined className="texto_azul font-size-16px me-2" />
              )}
              <span className="font-size-12px">
                {paso.numero}. {paso.titulo} —{" "}
                {paso.completo ? "Completado" : "Incompleto"}
              </span>
            </div>
          ))}
        </div>

        {!puedeInscribir && (
          <Alert
            className="mb-3"
            type="error"
            showIcon
            message={
              <span className="font-family-Montserrat-SemiBold font-size-14px">
                Esto hay que resolverlo para poder inscribir
              </span>
            }
            description={
              <ul className="mb-0 ps-3 font-size-12px">
                {impedimentos.map((impedimento, indice) => (
                  <li key={`${impedimento.motivo}-${indice}`} className="mb-1">
                    {impedimento.motivo}
                    {impedimento.paso && (
                      <Button
                        type="link"
                        className="p-0 ms-2 font-size-12px"
                        onClick={() => {
                          alCerrar();
                          irAlPaso(impedimento.paso as string);
                        }}
                      >
                        Ir a corregirlo
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            }
          />
        )}

        {/*
          Esto no impide inscribir: la propuesta se puede inscribir asi y
          corregirse dentro del plazo. Se muestra para que nadie la inscriba
          sin saber en que queda.
        */}
        {resumen.avisos.length > 0 && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message={
              <span className="font-family-Montserrat-SemiBold font-size-14px">
                Puedes inscribir así, pero ten en cuenta
              </span>
            }
            description={
              <div className="font-size-12px">
                <p className="mb-2">
                  {resumen.porRechazar > 0 &&
                    `${resumen.porRechazar} ${
                      resumen.porRechazar === 1
                        ? "cosa dejaría tu propuesta fuera"
                        : "cosas dejarían tu propuesta fuera"
                    }. `}
                  {resumen.porSubsanar > 0 &&
                    `${resumen.porSubsanar} ${
                      resumen.porSubsanar === 1 ? "te la pedirían" : "te las pedirían"
                    } subsanar.`}
                </p>
                <ul className="mb-0 ps-3">
                  {resumen.avisos.map((aviso, indice) => (
                    <li key={`${aviso.documento}-${indice}`} className="mb-1">
                      <span className="font-family-Montserrat-SemiBold">
                        {aviso.documento || "Documento sin nombre"}:
                      </span>{" "}
                      {aviso.texto}
                    </li>
                  ))}
                </ul>
              </div>
            }
          />
        )}

        {puedeInscribir && resumen.avisos.length === 0 && (
          <Alert
            className="mb-3"
            type="success"
            showIcon
            message={
              <span className="font-family-Montserrat-Regular font-size-12px">
                No vemos nada pendiente. Al inscribir, tu propuesta queda
                registrada y pasa a verificación.
              </span>
            }
          />
        )}

        <div className="d-flex justify-content-end" style={{ gap: 12 }}>
          <Button className="button-blanco-radius font-size-12px" onClick={alCerrar}>
            Seguir editando
          </Button>
          <Button
            className="button-azul-radius font-size-12px"
            onClick={alConfirmar}
            disabled={!puedeInscribir}
            loading={inscribiendo}
          >
            Inscribir propuesta
          </Button>
        </div>
      </div>
    </Modal>
  );
};
